import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import SortableCategoryRow, { CategoryRow, categoryShape } from './CategoryRow';

const csrfHeaders = () => {
  const tag = document.querySelector('meta[name="csrf-token"]');

  return tag ? { 'X-CSRF-Token': tag.content } : {};
};

const AddonCategories = ({ items, uncategorisedCount }) => {
  const [categories, setCategories] = useState(items);
  const [uncategorised, setUncategorised] = useState(uncategorisedCount);
  const [name, setName] = useState('');
  const [addError, setAddError] = useState(null);
  const [rowError, setRowError] = useState({});
  const [message, setMessage] = useState(null);

  const total = useMemo(
    () => categories.reduce((sum, category) => sum + (category.addon_count || 0), 0) + uncategorised,
    [categories, uncategorised],
  );

  const persistOrder = useCallback((ordered) => {
    axios.patch(
      '/admin/addon_categories/reorder',
      { items: ordered.map(({ id }) => ({ id })) },
      { headers: csrfHeaders() },
    ).catch(() => setMessage('The new order could not be saved. Reload the page and try again.'));
  }, []);

  // react-sortable swaps IN PLACE and hands back the same array reference, which
  // useState would treat as no change at all.
  const onSortItems = useCallback((ordered) => {
    const next = [...ordered];

    setCategories(next);
    persistOrder(next);
  }, [persistOrder]);

  const move = (category, delta) => {
    const index = categories.findIndex((row) => row.id === category.id);
    const target = index + delta;
    if (target < 0 || target >= categories.length) return;

    const next = [...categories];
    [next[index], next[target]] = [next[target], next[index]];

    setCategories(next);
    persistOrder(next);
    setMessage(`Moved “${category.name}” ${delta < 0 ? 'up' : 'down'}.`);
  };

  const add = (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    axios.post('/admin/addon_categories', { addon_category: { name: trimmed } }, { headers: csrfHeaders() })
      .then(({ data }) => {
        setCategories((current) => [...current, data.category]);
        setName('');
        setAddError(null);
        setMessage(`Added “${data.category.name}”.`);
      })
      .catch((error) => setAddError(error.response?.data?.error || 'That could not be added.'));
  };

  const rename = (category, nextName, done) => {
    if (!nextName || nextName === category.name) { done(); return; }

    axios.patch(
      `/admin/addon_categories/${category.id}`,
      { addon_category: { name: nextName } },
      { headers: csrfHeaders() },
    ).then(({ data }) => {
      setCategories((current) => current.map((row) => (row.id === category.id ? data.category : row)));
      setRowError((current) => ({ ...current, [category.id]: null }));
      setMessage(data.message);
      done();
    }).catch((error) => {
      setRowError((current) => ({
        ...current,
        [category.id]: error.response?.data?.error || 'That could not be saved.',
      }));
    });
  };

  const remove = (category, moveTo) => {
    axios.delete(`/admin/addon_categories/${category.id}`, {
      data: { move_to: moveTo || null },
      headers: csrfHeaders(),
    }).then(({ data }) => {
      const moved = category.addon_count || 0;

      setCategories((current) => current
        .filter((row) => row.id !== category.id)
        .map((row) => (String(row.id) === String(moveTo)
          ? { ...row, addon_count: (row.addon_count || 0) + moved }
          : row)));

      if (!moveTo) setUncategorised((current) => current + moved);
      setMessage(data.message);
    }).catch(() => setMessage(`“${category.name}” could not be deleted.`));
  };

  const rows = categories.map((category, index) => {
    const shared = {
      category,
      categories,
      first: index === 0,
      last: index === categories.length - 1,
      error: rowError[category.id],
      onRename: rename,
      onDelete: remove,
      onMoveUp: (row) => move(row, -1),
      onMoveDown: (row) => move(row, 1),
    };

    return (
      <SortableCategoryRow
        key={category.id}
        sortId={index}
        items={categories}
        onSortItems={onSortItems}
        {...shared}
      />
    );
  });

  return (
    <div className="aa-card">
      <form className="ac-add" onSubmit={add}>
        <label className="aa-label" htmlFor="ac-new-name">New category</label>
        <div className="ac-add-row">
          <input
            id="ac-new-name"
            className="aa-input"
            value={name}
            placeholder="New category name, like “Canvas prints”"
            onChange={(event) => { setName(event.target.value); setAddError(null); }}
          />
          <button type="submit" className="aa-btn aa-btn--primary" disabled={!name.trim()}>Add category</button>
        </div>
        {addError ? <p className="ac-error" role="alert">{addError}</p> : null}
      </form>

      {rows.length > 0 ? rows : (
        <div className="aa-empty">
          <h2>No categories yet</h2>
          <p>Add your first one above.</p>
        </div>
      )}

      <div className="aa-foot">
        <span>
          {`${categories.length} categor${categories.length === 1 ? 'y' : 'ies'}, `}
          {`${total} add-on${total === 1 ? '' : 's'} `}
          {`(${uncategorised} with no category)`}
        </span>
        <span className="ac-message" aria-live="polite">{message}</span>
      </div>
    </div>
  );
};

AddonCategories.propTypes = {
  items: PropTypes.arrayOf(categoryShape).isRequired,
  uncategorisedCount: PropTypes.number,
};

AddonCategories.defaultProps = {
  uncategorisedCount: 0,
};

export { CategoryRow };
export default AddonCategories;
